import * as React from 'react'
import { useLocalizeSafe } from '@/modules/shared/context/localize/LocalizeContext'
import { useChartT } from './i18n'

type Props = {
  onFilesSelected: (files: File[]) => void
  isUploading: boolean
  error?: string | null
}

export function StepUpload({ onFilesSelected, isUploading, error }: Props) {
  const T = useChartT()
  const { tSafe } = useLocalizeSafe()
  const [drag, setDrag] = React.useState(false)
  const [formatError, setFormatError] = React.useState<string | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // BMP only. JPG/PNG scans come back lossily compressed and the chart reader no
  // longer picks the traces out of them, so a non-BMP file would upload fine and
  // then silently report "no charts detected". The file dialog already filters to
  // .bmp; this guard covers drag & drop and the dialog's "All files" option.
  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? [])
    if (!files.length) return
    const bmp = files.filter((f) => /\.bmp$/i.test(f.name))
    setFormatError(
      bmp.length === files.length
        ? null
        : T(
            'onlyBmpSupported',
            'Obsługiwane są wyłącznie pliki BMP — w innych formatach obraz jest'
              + ' kompresowany stratnie i tarczki nie zostaną wykryte.',
          ),
    )
    if (bmp.length) onFilesSelected(bmp)
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-900">
        {T('uploadTitle', 'Wczytaj tarczki')}
      </h2>
      <p className="tw-mb-2 tw-text-sm tw-text-slate-600">
        {T(
          'uploadIntro',
          'Wczytaj pliki BMP ze skanami tarczek tachograficznych. Możesz wskazać kilka'
            + ' plików naraz. System wykryje tarczki i wczyta aktywności do edycji.',
        )}
      </p>
      <p className="tw-mb-4 tw-text-sm tw-text-slate-600">
        {T(
          'scanTips',
          'Wskazówki: skanuj w 300 dpi, najlepiej czarno-biało albo w skali szarości, z zamkniętą'
            + ' pokrywą (białe tło). Tarczki nie powinny na siebie nachodzić ani wystawać poza'
            + ' krawędź skanu.',
        )}
      </p>

      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDrag(true)
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDrag(false)
          handleFiles(e.dataTransfer.files)
        }}
        onClick={() => inputRef.current?.click()}
        className={[
          'tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center',
          'tw-rounded-xl tw-border tw-px-6 tw-py-8 tw-transition tw-duration-150 tw-ease-out',
          drag
            ? 'tw-border-blue-500 tw-bg-blue-50 tw-shadow-[0_0_0_2px_rgba(59,130,246,0.35)]'
            : 'tw-border-slate-200 tw-bg-white hover:tw-border-slate-300',
          isUploading ? 'tw-pointer-events-none tw-opacity-60' : '',
        ].join(' ')}
      >
        <div className="tw-mb-3 tw-text-3xl" aria-hidden="true">
          📄
        </div>
        <p className="tw-mb-1 tw-font-semibold tw-text-slate-900">
          {isUploading
            ? T('uploading', 'Wgrywanie...')
            : tSafe('importModule.import-files', 'Zaimportuj pliki')}
        </p>
        <p className="tw-mb-1 tw-text-sm tw-text-slate-500">
          {tSafe(
            'importModule.drag-or-pick',
            'Przeciągnij pliki tutaj lub wybierz z dysku',
          )}
        </p>
        <p className="tw-mb-4 tw-text-xs tw-text-slate-500">
          {T('acceptedFormats', 'BMP · do 50 MB')}
        </p>
        {/* Same button as the file import (PopupStepUploading). */}
        <button
          type="button"
          className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition hover:tw-bg-blue-700"
          onClick={(e) => {
            e.stopPropagation()
            inputRef.current?.click()
          }}
        >
          {tSafe('importModule.pick-from-disk', 'Wybierz z dysku')}
        </button>
        {/* BMP only — see handleFiles. WPF's OpenFileDialog for tarczki likewise
            defaults to the BMP filter (CardOpenFileDialog.cs →
            OpenFileDialogType.TachographChart, DefaultExt ".bmp"). Multiselect
            matches WPF (Multiselect = true for chart scans). */}
        <input
          ref={inputRef}
          type="file"
          accept=".bmp"
          multiple
          className="tw-hidden"
          onChange={(e) => {
            handleFiles(e.target.files)
            // Allow re-picking the same file(s) after a failed upload.
            e.target.value = ''
          }}
        />
      </div>

      {(formatError || error) && (
        <div className="tw-mt-4 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
          {formatError || error}
        </div>
      )}
    </div>
  )
}
